import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search, Package, Plus } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useClinic } from '@/contexts/ClinicContext'
import { clinicProgramService, ClinicProgram } from '@/services/clinicProgramService'
import { Skeleton } from '@/components/ui/skeleton'
import ClinicProgramFormDialog from '@/components/dialog/ClinicProgramFormDialog'
import ProgramCardByMode from '@/components/clinic-programs/ProgramCardByMode'

const ClinicProgramsPage = () => {
  const { clinic } = useClinic()
  const { toast } = useToast()
  const [programs, setPrograms] = useState<ClinicProgram[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const limit = 12

  const clinicArchetype = clinic?.clinic_archetype ?? null

  const fetchPrograms = async () => {
    if (!clinic?.id) return
    try {
      setIsLoading(true)
      const response = await clinicProgramService.getPrograms(clinic.id, {
        page,
        limit,
        search,
        sortBy: 'title',
        sortOrder: 'ASC',
      })
      setPrograms(response.programs)
      setTotalItems(response.pagination.totalItems)
    } catch (error) {
      console.error(error)
      toast({ title: 'Error', description: 'Failed to load programs', variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== search) {
        setPage(1)
        setSearch(searchInput.trim())
      }
    }, 800)
    return () => clearTimeout(handler)
  }, [searchInput])

  useEffect(() => {
    fetchPrograms()
  }, [clinic?.id, page, search])

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-montserrat text-slate-900 tracking-tight text-xl sm:text-2xl font-bold">
            Programs
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage your clinic's health programs
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg">
          <div className="w-8 h-8 bg-emerald-500 rounded-md flex items-center justify-center">
            <span className="text-sm font-bold text-white">{totalItems}</span>
          </div>
          <span className="text-xs font-medium text-emerald-700">Programs</span>
        </div>
      </div>

      <div className="flex flex-row items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search programs..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value.trimStart())}
            className="pl-10 bg-white border-slate-200 rounded-lg"
          />
        </div>
        <Button onClick={() => setCreateOpen(true)} className="whitespace-nowrap">
          <Plus className="h-4 w-4 mr-2" />
          Add Program
        </Button>
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="h-1 bg-slate-200" />
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && totalItems === 0 && (
        <div className="text-center py-20">
          <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-sm">
            {search ? 'No programs found matching your search' : 'No programs configured for your clinic'}
          </p>
          {search && (
            <Button variant="ghost" size="sm" className="mt-3 text-primary" onClick={() => setSearchInput('')}>
              Clear search
            </Button>
          )}
        </div>
      )}

      {!isLoading && programs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {programs.map((program, index) => (
            <ProgramCardByMode
              key={program.id}
              program={program}
              index={index}
              clinicId={clinic?.id || ''}
              onSuccess={fetchPrograms}
              clinicArchetype={clinicArchetype}
            />
          ))}
        </div>
      )}

      {!isLoading && totalItems > limit && (
        <div className="flex justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-3 text-sm text-slate-600">
            Page {page} of {Math.ceil(totalItems / limit)}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= Math.ceil(totalItems / limit)}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {clinic?.id && (
        <ClinicProgramFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          clinicId={clinic.id}
          mode="create"
          onSuccess={fetchPrograms}
          clinicArchetype={clinicArchetype}
        />
      )}
    </div>
  )
}

export default ClinicProgramsPage
